import PaywallLockV2 from 'components/organisms/PaywallLockV2/PaywallLockV2.component';
import useSubscriptionPlan from './useSubscriptionPlan';
import globalModalsController from 'components/organisms/GlobalModalsService/globalModalsController';
import type {
  LockContentType,
  SubscriptionCounterKeysEnum,
} from 'types/subscriptions.types';
import useFeatureCounts from './useFeatureCounts';
import { useCallback } from 'react';
import { isExpert, isWeb } from 'utils/constants';

const useFeatureLock = () => {
  const subscriptionPlan = useSubscriptionPlan();

  const { locks = {}, counters = {} } = subscriptionPlan?.features || {};

  const isFeatureLocked = (lockKey: string) =>
    locks[lockKey as keyof typeof locks] === true;

  const { data: featureCounts } = useFeatureCounts();

  const withPaywallLock = useCallback(
    (action: () => void, lockKey: string, lockContent: LockContentType) =>
      isWeb && isFeatureLocked(lockKey)
        ? globalModalsController.openModal({
            id: 'paywall-lock-v2',
            options: {
              width: '2xl',
              name: 'paywall-lock-v2',
              className: '!outline-none',
            },
            render: () => <PaywallLockV2 lockKey={lockKey} {...lockContent} />,
          })
        : action(),
    [isFeatureLocked],
  );

  const withPaywallCounterLock = useCallback(
    (
      action: () => void,
      counterKey: SubscriptionCounterKeysEnum,
      lockContent: LockContentType,
    ) => {
      const counter =
        (counters[counterKey as keyof typeof counters] as any)?.total || 0;

      const currentResourceCount = featureCounts?.[counterKey] || 0;

      if (
        isExpert ||
        counter > currentResourceCount ||
        counter === 'INFINITE'
      ) {
        return action();
      }

      return globalModalsController.openModal({
        id: 'paywall-lock-v2',
        options: {
          width: '2xl',
          name: 'paywall-lock-v2',
          className: '!outline-none',
        },
        render: () => (
          <PaywallLockV2
            counterKey={counterKey}
            currentCount={currentResourceCount}
            {...lockContent}
          />
        ),
      });
    },
    [counters, featureCounts],
  );

  return {
    isFeatureLocked,
    withPaywallLock,
    withPaywallCounterLock,
  };
};

export default useFeatureLock;
